/**
 * Single-slot overlay coordinator. Only ONE bottom-sheet can be open at a
 * time — the store holds a single nullable descriptor, so opening one
 * overlay structurally closes whatever was open. This makes "two sheets can
 * never coexist" true by construction instead of by timing hacks.
 *
 * App.svelte reads `activeOverlay` to drive `inert` on the whole app shell
 * (page + header + nav) while any overlay is open, so a synthesized ghost-click
 * on a header control after a sheet dismiss is absorbed (no-op) rather than
 * swallowed with a capture-phase listener.
 *
 * `share` is opened by the header's Share control (ShareSheet); `menu` by the
 * header's Menu control (MenuSheet, mounted by App.svelte beside OverlayHost).
 */
import { writable } from "svelte/store";
import type { GoalPreferences } from "@contract/types";
import { bridge } from "../bridge/client";
import type { MemberSwim } from "./clubSessions";

export type RingId = "week-distance" | "peak-hr" | "week-streak" | "day-streak";

export type ActiveOverlay =
  | { kind: "ring"; ring: RingId }
  | { kind: "share" }
  | { kind: "menu" }
  | { kind: "scores" }
  | { kind: "set-detail"; setIndex: number; fixFirst?: boolean; fixLapIndex?: number }
  | { kind: "sets-discovered" }
  /** The set page. `from` the ledger adds a Back pill that returns to it. */
  | { kind: "set-history"; setId: string; from?: "sets-discovered" }
  /** The month calendar. `club` (every member's swims, from the Club tab) turns
   *  it into the Club variant: a swimmer count per day. */
  | { kind: "your-swims"; club?: MemberSwim[] }
  | { kind: "suspect-deck" }
  /** "Fastest 25s · <Stroke>", from a stroke card's Best 25 tile. */
  | { kind: "fastest-25s"; stroke: string }
  /** The header's Beta pill (lane beta-panel). */
  | { kind: "beta" }
  | null;

export const activeOverlay = writable<ActiveOverlay>(null);

/**
 * Open (or replace) the single overlay slot. Setting the slot closes whatever
 * was open — mutual exclusion for free.
 */
export function openOverlay(next: Exclude<ActiveOverlay, null>): void {
  activeOverlay.set(next);
}

/**
 * The Club calendar re-derives while it is open (B-020, build 12). `openOverlay`
 * stores the swims as they were at the tap, and the club snapshot lands ~2 s after
 * launch: a sheet opened before it kept counting only the session source (Sep 4 = 2,
 * Sep 2 = 1) until it was closed and reopened. ClubTab calls this whenever its merged
 * swims change; it swaps them into an OPEN Club calendar only. The Your swims
 * calendar and any other overlay are left alone.
 */
export function refreshOpenClubCalendar(club: MemberSwim[]): void {
  activeOverlay.update((cur) => (cur?.kind === "your-swims" && cur.club && cur.club !== club ? { kind: "your-swims", club } : cur));
}

/**
 * Close the overlay IF the currently-open one matches `kind`. A stale close
 * from an overlay that has already been replaced by another is a no-op, so a
 * late dismiss can't tear down whatever opened after it.
 */
export function closeOverlay(
  kind:
    | "ring"
    | "share"
    | "menu"
    | "scores"
    | "set-detail"
    | "sets-discovered"
    | "set-history"
    | "your-swims"
    | "suspect-deck"
    | "fastest-25s"
    | "beta",
): void {
  activeOverlay.update((cur) => (cur?.kind === kind ? null : cur));
}

/**
 * Monotonic counter bumped whenever goals are saved. MetricRings reacts to it
 * to refetch goals after a save (replaces the old onSaved callback wiring,
 * which is gone now that RingConfigSheet is mounted by OverlayHost, not by
 * MetricRings).
 */
export const goalsVersion = writable(0);

export function bumpGoalsVersion(): void {
  goalsVersion.update((n) => n + 1);
}

/**
 * 4-week median weekly distance (km), or null when there isn't enough history.
 * Published by MetricRings (which owns the workout fetch) and read by
 * OverlayHost to feed the Week Distance ring sheet its suggestion. Lives here
 * because OverlayHost — not MetricRings — now mounts the sheet, so the value
 * has to cross the component boundary.
 */
export const medianWeekKm = writable<number | null>(null);

/**
 * The swimmer's saved goals, shared by the top-bar scores ring and the scores
 * drawer (tabs slice, 2026-09-10). null = not loaded yet, or no shell to ask.
 *
 * Each native tab is its own web view, so a goal saved in one never reaches
 * another's store: App.svelte calls `refreshGoalPrefs` on mount and on
 * `swimset:tab-appeared`. A save in THIS view sets the store directly (the
 * durable write is journaled and lands in the background, so a read-back right
 * after the save could still return the old goal).
 */
export const goalPrefs = writable<GoalPreferences | null>(null);

let goalsFetchSeq = 0;

/** Re-read goals from the shell. Sequence-guarded: a slow earlier read never
 *  overwrites a newer one, and a failed read keeps what the store had. */
export async function refreshGoalPrefs(): Promise<void> {
  if (!bridge.isAvailable()) return;
  const seq = ++goalsFetchSeq;
  try {
    const got = await bridge.getGoals();
    if (seq === goalsFetchSeq) goalPrefs.set(got);
  } catch {
    // keep the last-known goals
  }
}

/** Save a goal in this view: the store updates now, so the ring does too.
 *  Bumps the fetch sequence so an in-flight read cannot undo the save. */
export function setGoalPrefs(next: GoalPreferences): void {
  goalsFetchSeq++;
  goalPrefs.set(next);
  bumpGoalsVersion();
}

export interface WeekGoalRing {
  /** Arc fraction 0..1, or null when no goal is set (track only, no arc). */
  fill: number | null;
  /** This week reached the goal: the arc is full and wears --win. */
  met: boolean;
  /** Whole percent of the goal (can pass 100), or null with no goal. */
  percent: number | null;
  /** What VoiceOver says for the ring button. */
  label: string;
}

/**
 * The top-bar ring, pure. Arc honesty (persona gate 2026-07-10, the same rule
 * HistoryRings follows): a partial arc claims "% of a target", so it is drawn
 * only against a goal the swimmer set, never a default.
 */
export function weekGoalRing(distanceM: number, goalM: number | null | undefined): WeekGoalRing {
  const m = Math.max(0, Math.round(distanceM));
  if (goalM == null || !(goalM > 0)) {
    return {
      fill: null,
      met: false,
      percent: null,
      label: `Scores. This week ${m.toLocaleString("en-US")} m. No weekly goal set.`,
    };
  }
  const percent = Math.round((m / goalM) * 100);
  const met = m >= goalM;
  return {
    fill: Math.min(1, m / goalM),
    met,
    percent,
    label: `Scores. This week ${m.toLocaleString("en-US")} of ${Math.round(goalM).toLocaleString("en-US")} m, ${percent}%${met ? ", goal met" : ""}.`,
  };
}
